import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Lock, Mail, Eye, EyeOff, Anchor } from "lucide-react";

export default function LoginPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    for (let attempt = 0; attempt < 3; attempt++) {
      try {
        await login(email.trim().toLowerCase(), password);
        setLoading(false);
        return;
      } catch (err) {
        if ((err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") && attempt < 2) {
          await new Promise(r => setTimeout(r, 2000 * (attempt + 1)));
          continue;
        }
        const status = err.response?.status;
        const detail = err.response?.data?.detail;
        if (status === 401) {
          setError("Email veya sifre hatali. Lutfen kontrol edin.");
        } else if (detail && typeof detail === "string") {
          setError(detail);
        } else if (err.code === "ERR_NETWORK" || err.code === "ECONNABORTED") {
          setError("Sunucuya baglanilamiyor. Lutfen tekrar deneyin.");
        } else {
          setError("Beklenmedik hata. Lutfen sayfayi yenileyip tekrar deneyin.");
        }
        break;
      }
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0E1A] flex items-center justify-center p-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo & Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-500 to-blue-700 mb-4">
            <Anchor className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Mega Lufer</h1>
          <p className="text-sm text-slate-500 mt-1">Yonetim Paneli</p>
        </div>

        {/* Login Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white/[0.03] border border-white/[0.06] backdrop-blur-xl rounded-2xl p-8 space-y-5"
          data-testid="login-form"
        >
          <div className="text-center mb-2">
            <h2 className="text-lg font-semibold text-white">Giris Yap</h2>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/20 text-red-400 text-sm px-4 py-3 rounded-lg" data-testid="login-error">
              {error}
            </div>
          )}

          <div className="space-y-1.5">
            <label className="text-xs text-slate-500 font-medium">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="admin@lufertour.com"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="username"
                inputMode="email"
                spellCheck={false}
                style={{ fontSize: 16 }}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-10 pr-4 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 transition-colors"
                required
                data-testid="login-email-input"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-xs text-slate-500 font-medium">Sifre</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sifrenizi girin"
                autoCapitalize="none"
                autoCorrect="off"
                autoComplete="current-password"
                spellCheck={false}
                style={{ fontSize: 16 }}
                className="w-full bg-white/[0.04] border border-white/[0.08] rounded-lg pl-10 pr-10 py-3 text-base text-white placeholder:text-slate-600 focus:outline-none focus:border-blue-500/50 transition-colors"
                required
                data-testid="login-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 disabled:bg-blue-600/50 text-white font-medium py-2.5 rounded-lg text-sm transition-colors"
            data-testid="login-submit-button"
          >
            {loading ? "Giris yapiliyor..." : "Giris Yap"}
          </button>
        </form>

        <p className="text-center text-xs text-slate-700 mt-6">
          Mega Lufer Yachts - Booking Management System
        </p>
      </div>
    </div>
  );
}
